import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import {
  RefreshIcon,
  UsersIcon,
  SearchIcon,
  CancelIcon,
  SaveIcon,
} from "../components/icons";
import { formatDate } from "../utils/dateUtils";

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Declined",
  ended: "Ended",
};

const statusPillClass = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  ended: "bg-gray-100 text-gray-700",
};

const MyBureau = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Review state for the active association (or most recent ended).
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewSavedAt, setReviewSavedAt] = useState(null);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading your bureau status…");
      setGlobalLoading(true);
      const res = await apiFetch("/public/bureau-associations/me");
      setItems(res?.data || []);
      // Clear the unread badge on visit.
      apiFetch("/public/bureau-associations/mark-seen", {
        method: "POST",
      }).catch(() => {});
      window.dispatchEvent(new CustomEvent("associations:seen"));
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    load();
  }, [load]);

  // Reviewable = the most recent approved or ended association. Pending
  // and rejected don't qualify (seeker was never really a client).
  const reviewable =
    items.find((it) => it.status === "approved") ||
    items.find((it) => it.status === "ended");

  // Pull any existing review onto the form when reviewable changes.
  useEffect(() => {
    if (!reviewable) {
      setReviewRating(0);
      setReviewComment("");
      return;
    }
    let cancelled = false;
    apiFetch(`/public/bureaus/${reviewable.bureau._id}/reviews?limit=50`)
      .then((res) => {
        if (cancelled) return;
        // Look up this seeker's review by name match isn't reliable —
        // the public endpoint doesn't expose publicUser id. Instead we
        // just leave the form blank; if the user has already submitted
        // a review the POST acts as upsert, so they can update it freely.
        // (A dedicated "my-review" endpoint could be added later.)
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [reviewable]);

  const cancelPending = async (item) => {
    if (!window.confirm("Cancel this pending request?")) return;
    setBusy(true);
    try {
      await apiFetch(`/public/bureau-associations/${item._id}`, {
        method: "DELETE",
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  const disassociate = async (item) => {
    if (
      !window.confirm(
        `End your association with ${
          item.bureau?.name || "this bureau"
        }? You can request another bureau afterwards.`
      )
    )
      return;
    setBusy(true);
    try {
      await apiFetch("/public/bureau-associations/disassociate", {
        method: "POST",
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to disassociate");
    } finally {
      setBusy(false);
    }
  };

  const submitReview = async () => {
    if (!reviewable) return;
    if (reviewRating < 1 || reviewRating > 5) {
      setError("Please pick a rating from 1 to 5.");
      return;
    }
    setReviewSaving(true);
    setError("");
    try {
      await apiFetch(`/public/bureaus/${reviewable.bureau._id}/review`, {
        method: "POST",
        body: JSON.stringify({
          rating: reviewRating,
          comment: reviewComment,
        }),
      });
      setReviewSavedAt(new Date().toISOString());
    } catch (err) {
      setError(err.message || "Failed to save review");
    } finally {
      setReviewSaving(false);
    }
  };

  const deleteReview = async () => {
    if (!reviewable) return;
    if (!window.confirm("Delete your review for this bureau?")) return;
    setReviewSaving(true);
    try {
      await apiFetch(`/public/bureaus/${reviewable.bureau._id}/review`, {
        method: "DELETE",
      });
      setReviewRating(0);
      setReviewComment("");
      setReviewSavedAt(null);
    } catch (err) {
      setError(err.message || "Failed to delete review");
    } finally {
      setReviewSaving(false);
    }
  };

  const active = items.find(
    (it) => it.status === "approved" || it.status === "pending"
  );

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <UsersIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                My Bureau
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Manage your bureau association and reviews
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {/* Empty state — no associations yet */}
        {!loading && items.length === 0 && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center">
            <p className="text-sm text-[var(--color-text-muted)] mb-3">
              You're not associated with a bureau yet. Browse the directory
              to find one that suits you.
            </p>
            <button
              onClick={() => navigate("/public-profiles")}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold transition-colors"
            >
              <SearchIcon className="w-4 h-4" />
              Browse bureaus
            </button>
          </div>
        )}

        {/* Active / pending card */}
        {active && (
          <div
            className={`rounded-lg border-2 p-4 ${
              active.status === "approved"
                ? "border-[var(--color-primary)] bg-[var(--color-primary-soft)]"
                : "border-[var(--color-border)] bg-[var(--color-surface)]"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  {active.status === "approved"
                    ? "Your bureau"
                    : "Pending request"}
                </div>
                <h2 className="text-lg font-semibold text-[var(--color-text-strong)] truncate">
                  {active.bureau?.name || "—"}
                </h2>
                <div className="text-xs text-[var(--color-text-muted)]">
                  {active.bureau?.ownerName && (
                    <>Owner: {active.bureau.ownerName} · </>
                  )}
                  {active.bureau?.contactNumber && (
                    <a
                      href={`tel:${active.bureau.contactNumber}`}
                      className="hover:underline"
                    >
                      {active.bureau.contactNumber}
                    </a>
                  )}
                </div>
                {active.bureau?.address && (
                  <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    {active.bureau.address}
                  </div>
                )}
              </div>
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  statusPillClass[active.status]
                }`}
              >
                {STATUS_LABEL[active.status]}
              </span>
            </div>

            {active.message && (
              <div className="text-xs text-[var(--color-text-muted)] mt-2">
                <span className="font-semibold">Your note:</span>{" "}
                <span className="whitespace-pre-wrap">{active.message}</span>
              </div>
            )}
            {active.bureauResponse && (
              <div className="text-xs text-[var(--color-text-muted)] mt-1">
                <span className="font-semibold">Bureau response:</span>{" "}
                <span className="whitespace-pre-wrap">
                  {active.bureauResponse}
                </span>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {active.status === "pending" && (
                <button
                  onClick={() => cancelPending(active)}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
                >
                  <CancelIcon className="w-4 h-4" />
                  Cancel request
                </button>
              )}
              {active.status === "approved" && (
                <button
                  onClick={() => disassociate(active)}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-red-50 hover:border-red-300 text-red-600 disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
                >
                  <CancelIcon className="w-4 h-4" />
                  Disassociate from this bureau
                </button>
              )}
            </div>

            <div className="mt-3 text-[10px] text-[var(--color-text-muted)]">
              Requested {formatDate(active.createdAt)}
              {active.respondedAt && (
                <> · {STATUS_LABEL[active.status]} {formatDate(active.respondedAt)}</>
              )}
            </div>
          </div>
        )}

        {/* Review form — visible whenever the seeker has a current or
            past approved association. */}
        {reviewable && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
              Rate & review
            </div>
            <h3 className="text-base font-semibold text-[var(--color-text-strong)] mb-1">
              How was your experience with {reviewable.bureau?.name}?
            </h3>
            <p className="text-xs text-[var(--color-text-muted)] mb-3">
              Your rating and review are visible to everyone browsing
              bureaus. Posting again replaces your previous review.
            </p>

            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((v) => {
                const filled = v <= reviewRating;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setReviewRating(v)}
                    aria-label={`${v} star${v === 1 ? "" : "s"}`}
                    className="p-1"
                  >
                    {/* Single star per button — the row of 5 buttons IS
                        the 5-star scale. */}
                    <svg
                      viewBox="0 0 24 24"
                      className={`h-6 w-6 ${
                        filled
                          ? "text-yellow-500 fill-yellow-500"
                          : "text-[var(--color-text-muted)] fill-none"
                      }`}
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <span className="sr-only">{v}</span>
                  </button>
                );
              })}
              <span className="text-xs text-[var(--color-text-muted)] ml-2">
                {reviewRating ? `${reviewRating}/5` : "Tap a star"}
              </span>
            </div>

            <textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              rows={3}
              placeholder="Share your honest experience (optional)"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
            />

            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={submitReview}
                disabled={reviewSaving || reviewRating < 1}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <SaveIcon className="w-4 h-4" />
                {reviewSaving ? "Saving…" : "Save review"}
              </button>
              <button
                type="button"
                onClick={deleteReview}
                disabled={reviewSaving}
                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-xs font-medium transition-colors text-[var(--color-text-muted)]"
              >
                Delete my review
              </button>
              {reviewSavedAt && (
                <span className="text-[10px] text-[var(--color-text-muted)]">
                  Saved {formatDate(reviewSavedAt)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* History */}
        {items.length > 0 && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
              History
            </div>
            <ul className="space-y-2 text-sm">
              {items.map((it) => (
                <li
                  key={it._id}
                  className="flex items-baseline justify-between gap-3"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium">
                      {it.bureau?.name || "—"}
                    </span>{" "}
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full ml-1 ${
                        statusPillClass[it.status]
                      }`}
                    >
                      {STATUS_LABEL[it.status]}
                    </span>
                  </span>
                  <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
                    {formatDate(it.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBureau;
